<template>
  <div class="row items-center q-gutter-sm cursor-pointer">
    <!-- Away/Available button -->
    <q-btn
      flat
      round
      size="sm"
      :icon="awayIcon"
      :class="isAway ? 'sa-agent-is-away' : 'sa-agent-not-away'"
      @click="toggleAway"
    >
      <s-tooltip class="text-base" position="top">{{awayLabel}}</s-tooltip>
    </q-btn>

    <!-- Voice/Phone button -->
    <q-btn
      v-if="hasWorkerId && phoneEntitlement.isEntitled"
      flat
      round
      size="sm"
      :icon="voiceIcon"
      :class="isAwayFromPhone ? 'sa-agent-is-away' : 'sa-agent-not-away'"
      @click="toggleVoice"
    >
      <s-tooltip position="top" class="text-base">{{voiceLabel}}</s-tooltip>
    </q-btn>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useQuasar } from 'quasar'
import { useI18n } from 'vue-i18n'
import { useGraphqlSdk } from 'src/graphql/graphql-client'
import { useFeatureEntitlements } from '@frontegg/vue'
import { Entitlements } from 'src/shared/entitlements'
import { agentSettingsStore } from 'src/stores/agent-settings-global-store'
import { agentWithStatusStore } from 'src/features/agent/agent-avatar-with-status/agent-with-status-global-store'
import STooltip from "components/STooltip.vue";

// --- your original state & computed values ---
const $q = useQuasar()
const { t } = useI18n()
const phoneEntitlement = useFeatureEntitlements(Entitlements.PhoneService)

const agentSettingsStoreState = agentSettingsStore.getState()
const agentWithStatusStoreState = agentWithStatusStore.getList()

const agentId = computed(() => agentSettingsStoreState.id)
const isAway = computed(() => agentWithStatusStoreState.find(i => i.id === agentId.value)?.away ?? false)
const isAwayFromPhone = computed(() => agentWithStatusStoreState.find(i => i.id === agentId.value)?.awayFromPhone ?? false)
const hasWorkerId = computed(() => !!agentWithStatusStoreState.find(i => i.id === agentId.value)?.workerId)

// --- labels & icons ---
const awayLabel = computed(() =>
  isAway.value
    ? t('agent.awayStatus.setYourselfAsAvailable')
    : t('agent.awayStatus.setYourselfAsAway')
)
const voiceLabel = computed(() =>
  isAwayFromPhone.value
    ? t('agent.awayFromPhoneStatus.setYourselfAsAvailable')
    : t('agent.awayFromPhoneStatus.setYourselfAsAway')
)

const awayIcon = computed(() =>
  isAway.value ? 'fa fa-hand-wave' : 'fa fa-hand-peace'
)
const voiceIcon = computed(() =>
  isAwayFromPhone.value
    ? 'fa fa-phone-xmark'
    : 'fa fa-phone-arrow-down-left'
)

// --- mutations with Quasar notifications ---
async function toggleAway() {
  const progress = $q.notify({
    type: 'ongoing',
    message: isAway.value
      ? t('agent.awayStatus.isSettingAvailableProgressText')
      : t('agent.awayStatus.isSettingAwayProgressText'),
  })

  try {
    const sdk = useGraphqlSdk()
    await sdk.SetAgentAwayStatus({
      input: {
        input: {
          agentId: agentId.value,
          away: !isAway.value
        }
      }
    })
    progress({
      type: 'positive',
      message: t('common.changesSavedLong'),
    })
  }
  catch (err) {
    console.error(err)
    progress({
      type: 'negative',
      message: t('common.saveChangesGenericError'),
      timeout: 3000
    })
  }
}

async function toggleVoice() {
  const progress = $q.notify({
    type: 'ongoing',
    message: isAwayFromPhone.value
      ? t('agent.awayStatus.isSettingAvailableProgressText')
      : t('agent.awayStatus.isSettingAwayProgressText'),
  })

  try {
    const sdk = useGraphqlSdk()
    await sdk.SetAgentAwayStatus({
      input: {
        input: {
          agentId: agentId.value,
          awayFromPhone: !isAwayFromPhone.value
        }
      }
    })
    progress({
      type: 'positive',
      message: t('common.changesSavedLong'),
    })
  }
  catch (err) {
    console.error(err)
    progress({
      type: 'negative',
      message: t('common.saveChangesGenericError'),
      timeout: 3000
    })
  }
}
</script>

<style lang="sass"/>
